import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebaseDDH';
import { listClients, clientLabel, clientName } from '../../utils/clients';
import { deleteQuote, formatMoney, formatQuoteDate, formatStatus, isExpired, withdrawQuote } from '../../utils/quotes';
import ClientInvoices from './ClientInvoices';
import ClientNotes from './ClientNotes';
import { downloadSignedQuote } from '../../utils/quotePdf';
import { MONO } from '../../constants/fonts';
import * as ds from '../../constants/dashboardStyles';

const styles = {
  card: ds.card,
  title: ds.cardTitle,
  description: ds.cardDescription,
  select: { ...ds.select, marginBottom: 0 },
  list: ds.list,
  listItem: ds.listItem,
  listMain: ds.listMain,
  listMeta: ds.listMeta,
  badge: ds.badge,
  empty: ds.empty,
  error: ds.error,
  removeButton: ds.iconButton,
  withdrawButton: {
    padding: '5px 10px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#555',
    fontSize: '0.75rem',
    cursor: 'pointer',
    flexShrink: 0,
  },
  // The armed row breaks out of the normal flex layout so the warning has
  // room to be a full sentence rather than a bare pair of buttons.
  listItemConfirming: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
    background: '#fdf0ee',
    border: '1px solid #f0c9c3',
  },
  confirmText: {
    fontSize: '0.82rem',
    color: '#8c2f22',
    lineHeight: 1.5,
  },
  confirmRow: {
    display: 'flex',
    gap: 6,
    flexShrink: 0,
  },
  confirmDelete: {
    padding: '5px 10px',
    borderRadius: 6,
    border: 'none',
    background: '#c0392b',
    color: '#fff',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmCancel: {
    padding: '5px 10px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#555',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  download: {
    padding: '6px 10px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#333',
    fontSize: '0.85rem',
    cursor: 'pointer',
    flexShrink: 0,
  },
  stats: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 20,
  },
  stat: {
    flex: 1,
    minWidth: 100,
    padding: '12px 14px',
    background: '#f9f9f7',
    border: '1px solid #ececE6',
    borderRadius: 10,
  },
  statLabel: {
    fontSize: '0.7rem',
    color: '#888',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
  statValue: {
    fontFamily: MONO,
    fontSize: '1.05rem',
    fontWeight: 700,
    marginTop: 4,
  },
};

const BADGE_COLORS = ds.BADGE_COLORS;

// Without this a just-sent quote reads back with a null createdAt and lands at
// the wrong end of the list until the server confirms the write.
const ESTIMATE = { serverTimestamps: 'estimate' };

// `invoices` comes from AdminScreen, which watches them for the Outstanding
// tab and its badge — one listener rather than one per panel.
export default function ClientsPanel({ invoices = [] }) {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [error, setError] = useState('');
  // Withdrawing takes two clicks: the × arms the row, the second confirms.
  // Accepted quotes show a download instead — they can't be deleted at all.
  const [confirmingId, setConfirmingId] = useState(null);

  const remove = async (id) => {
    setConfirmingId(null);
    try {
      await deleteQuote(id);
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    }
  };

  useEffect(() => {
    listClients()
      .then(setClients)
      .catch((err) => setError(err.message.replace('Firebase: ', '')));
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'quotes'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => setQuotes(snapshot.docs.map((d) => ({ id: d.id, ...d.data(ESTIMATE) }))),
      (err) => setError(err.message.replace('Firebase: ', '')),
    );
    return unsubscribe;
  }, []);

  // Every quote is already in memory for the admin list, so filtering here
  // costs nothing and avoids a second per-client query.
  const clientQuotes = useMemo(
    () => quotes.filter((quote) => quote.clientEmail === selected),
    [quotes, selected],
  );

  const stats = useMemo(() => ({
    total: clientQuotes.length,
    awaiting: clientQuotes.filter((q) => q.status === 'sent').length,
    accepted: clientQuotes.filter((q) => q.status === 'accepted').length,
    acceptedValue: clientQuotes
      .filter((q) => q.status === 'accepted')
      .reduce((sum, q) => sum + (q.total || 0), 0),
  }), [clientQuotes]);

  return (
    <div style={styles.card}>
      <div style={styles.title}>Clients</div>
      <p style={styles.description}>Pick a client to see everything you've sent them and where each quote stands.</p>

      <select style={styles.select} value={selected} onChange={(e) => setSelected(e.target.value)}>
        <option value="">Select a client…</option>
        {clients.map((client) => (
          <option key={client.id} value={client.email}>{clientLabel(client)}</option>
        ))}
      </select>

      {error && <div style={styles.error}>{error}</div>}

      {!error && clients.length === 0 && (
        <p style={styles.empty}>No clients yet — create one at the bottom of this page.</p>
      )}

      {selected && (
        <>
          <div style={styles.stats}>
            <div style={styles.stat}>
              <div style={styles.statLabel}>Quotes</div>
              <div style={styles.statValue}>{stats.total}</div>
            </div>
            <div style={styles.stat}>
              <div style={styles.statLabel}>Awaiting</div>
              <div style={styles.statValue}>{stats.awaiting}</div>
            </div>
            <div style={styles.stat}>
              <div style={styles.statLabel}>Accepted</div>
              <div style={styles.statValue}>{stats.accepted}</div>
            </div>
            <div style={styles.stat}>
              <div style={styles.statLabel}>Accepted value</div>
              <div style={styles.statValue}>{formatMoney(stats.acceptedValue)}</div>
            </div>
          </div>

          <ClientNotes clientEmail={selected} />

          {clientQuotes.length === 0 ? (
            <p style={styles.empty}>Nothing sent to this client yet.</p>
          ) : (
            <ul style={styles.list}>
              {clientQuotes.map((quote) => (
                confirmingId === quote.id ? (
                  <li key={quote.id} style={{ ...styles.listItem, ...styles.listItemConfirming }}>
                    <div style={styles.confirmText}>
                      Delete <strong>{quote.title}</strong>?
                      {' '}It disappears from {clientName(clients.find((c) => c.email === selected)) || quote.clientEmail}'s
                      account page too, and can't be undone.
                    </div>
                    <div style={styles.confirmRow}>
                      <button type="button" style={styles.confirmDelete} onClick={() => remove(quote.id)}>
                        Yes, delete it
                      </button>
                      <button type="button" style={styles.confirmCancel} onClick={() => setConfirmingId(null)}>
                        Cancel
                      </button>
                    </div>
                  </li>
                ) : (
                  <li key={quote.id} style={styles.listItem}>
                    <div style={styles.listMain}>
                      <div>{quote.title}</div>
                      <div style={styles.listMeta}>
                        {formatMoney(quote.total)}
                        {formatQuoteDate(quote.createdAt) && ` · sent ${formatQuoteDate(quote.createdAt)}`}
                        {quote.signedName && ` · signed by ${quote.signedName}`}
                        {quote.contractUrl && ' · contract attached'}
                        {isExpired(quote) && ` · expired ${formatQuoteDate(quote.expiresAt)}`}
                        {quote.status === 'sent' && !isExpired(quote) && quote.expiresAt
                          && ` · valid until ${formatQuoteDate(quote.expiresAt)}`}
                      </div>
                    </div>
                    <span style={{ ...styles.badge, ...(BADGE_COLORS[quote.status] || {}) }}>{formatStatus(quote.status)}</span>
                    {quote.status === 'accepted' ? (
                      <button
                        type="button"
                        style={styles.download}
                        onClick={() => downloadSignedQuote(quote)}
                        title="Download signed copy"
                        aria-label={`Download signed copy of ${quote.title}`}
                      >
                        ⤓
                      </button>
                    ) : (
                      <>
                        {quote.status === 'sent' && (
                          <button
                            type="button"
                            style={styles.withdrawButton}
                            onClick={() => withdrawQuote(quote.id).catch((e) => setError(e.message))}
                            title="Mark as no longer available — the client keeps seeing it but can't accept"
                          >
                            Withdraw
                          </button>
                        )}
                        <button
                          type="button"
                          style={styles.removeButton}
                          onClick={() => setConfirmingId(quote.id)}
                          aria-label={`Delete quote ${quote.title}`}
                          title="Delete this quote"
                        >
                          &times;
                        </button>
                      </>
                    )}
                  </li>
                )
              ))}
            </ul>
          )}

          <ClientInvoices
            clientEmail={selected}
            clientName={clients.find((c) => c.email === selected)?.name || null}
            quotes={clientQuotes}
            invoices={invoices.filter((invoice) => invoice.clientEmail === selected)}
          />
        </>
      )}
    </div>
  );
}
